import React from 'react';
import { Button, Grid, Header, Message, Segment } from 'semantic-ui-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCount } from './utils';

const REQUEST_COLORS = {
  text: '#2185d0',
  image: '#a333c8',
  video: '#00b5ad',
  unknown: '#767676',
};

const statusFill = (statusCode) => {
  if (statusCode >= 500) return '#db2828';
  if (statusCode >= 400) return '#f2711c';
  if (statusCode >= 200 && statusCode < 300) return '#fbbd08';
  return '#767676';
};

const requestLabel = (requestType) =>
  ({ text: '文本', image: '图片', video: '视频' }[requestType] ||
  requestType ||
  '未知');

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className='error-chart-tooltip'>
      <strong>{label}</strong>
      <span>失败：{formatCount(payload[0].value)}</span>
    </div>
  );
};

const DistributionTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <div className='error-chart-tooltip'>
      <strong>{item.label || item.name}</strong>
      <span>失败：{formatCount(item.value)}</span>
    </div>
  );
};

const TrendChart = ({ trend, bucketUnit }) => {
  const data = (trend || []).map((item) => ({
    bucket: item.bucket,
    count: Number(item.failure_count || 0),
  }));

  return (
    <Segment className='error-stats-panel'>
      <Header as='h3'>
        失败趋势
        <Header.Subheader>
          按{bucketUnit === 'hour' ? '小时' : '天'}聚合
        </Header.Subheader>
      </Header>
      {data.length === 0 ? (
        <Message info content='当前时间范围没有失败记录' />
      ) : (
        <ResponsiveContainer width='100%' height={300}>
          <AreaChart data={data} margin={{ top: 12, right: 16, left: 4 }}>
            <defs>
              <linearGradient id='failureTrendFill' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='5%' stopColor='#db2828' stopOpacity={0.28} />
                <stop offset='95%' stopColor='#db2828' stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray='3 3' stroke='#edf0f3' />
            <XAxis
              dataKey='bucket'
              tick={{ fontSize: 11 }}
              minTickGap={24}
              tickFormatter={(value) =>
                bucketUnit === 'hour' ? value.slice(5) : value.slice(5)
              }
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => formatCount(value)}
            />
            <Tooltip content={<TrendTooltip />} />
            <Area
              type='monotone'
              dataKey='count'
              name='失败数'
              stroke='#db2828'
              strokeWidth={2}
              fill='url(#failureTrendFill)'
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Segment>
  );
};

const DistributionCharts = ({
  requestTypes,
  statusCodes,
  onRequestTypeClick,
  onStatusCodeClick,
}) => {
  const requestData = (requestTypes || []).map((item) => ({
    name: item.request_type || 'unknown',
    label: requestLabel(item.request_type),
    value: Number(item.failure_count || 0),
  }));
  const statusData = (statusCodes || []).slice(0, 8).map((item) => ({
    name: String(item.status_code),
    label:
      item.status_code >= 200 && item.status_code < 300
        ? `${item.status_code} 业务失败`
        : String(item.status_code),
    statusCode: item.status_code,
    value: Number(item.failure_count || 0),
  }));

  return (
    <Segment className='error-stats-panel'>
      <Header as='h3'>失败分布</Header>
      <div className='error-distribution-chart'>
        <ResponsiveContainer width='100%' height={150}>
          <PieChart>
            <Pie
              data={requestData}
              dataKey='value'
              nameKey='label'
              innerRadius={34}
              outerRadius={58}
              paddingAngle={3}
              onClick={(item) =>
                onRequestTypeClick?.(item.payload?.name || item.name)
              }
            >
              {requestData.map((item) => (
                <Cell
                  key={item.name}
                  fill={REQUEST_COLORS[item.name] || REQUEST_COLORS.unknown}
                  className='clickable-chart-item'
                />
              ))}
            </Pie>
            <Tooltip content={<DistributionTooltip />} />
            <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
        <div className='error-chart-drill-actions'>
          {requestData.map((item) => (
            <Button
              key={item.name}
              type='button'
              size='mini'
              basic
              onClick={() => onRequestTypeClick?.(item.name)}
            >
              {item.label} {formatCount(item.value)}
            </Button>
          ))}
        </div>
        <ResponsiveContainer width='100%' height={150}>
          <BarChart
            data={statusData}
            layout='vertical'
            margin={{ top: 4, right: 18, left: 8, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray='3 3' horizontal={false} />
            <XAxis type='number' hide />
            <YAxis
              type='category'
              dataKey='label'
              width={76}
              tick={{ fontSize: 11 }}
            />
            <Tooltip content={<DistributionTooltip />} />
            <Bar
              dataKey='value'
              radius={[0, 4, 4, 0]}
              onClick={(item) =>
                onStatusCodeClick?.(item.payload?.statusCode ?? item.statusCode)
              }
            >
              {statusData.map((item) => (
                <Cell
                  key={item.name}
                  fill={statusFill(item.statusCode)}
                  className='clickable-chart-item'
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className='error-chart-drill-actions'>
          {statusData.map((item) => (
            <Button
              key={item.name}
              type='button'
              size='mini'
              basic
              onClick={() => onStatusCodeClick?.(item.statusCode)}
            >
              {item.label} {formatCount(item.value)}
            </Button>
          ))}
        </div>
      </div>
    </Segment>
  );
};

const Charts = (props) => (
  <Grid stackable columns={16}>
    <Grid.Column width={10}>
      <TrendChart trend={props.trend} bucketUnit={props.bucketUnit} />
    </Grid.Column>
    <Grid.Column width={6}>
      <DistributionCharts {...props} />
    </Grid.Column>
  </Grid>
);

export default Charts;
